/**
 * History strip — 90 days by default, month/week granularity toggle, and a
 * "Load more" that pages ~60 days further back per click. Data is retained
 * indefinitely, so there is always more to load until the series runs out.
 */

import { useTranslation } from 'react-i18next';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { FinopsHistory } from '../../../../shared/types';
import { EmptyState, fmtDay, fmtMonth, fmtUSD, fmtUSDCents, fmtWeekRange } from './shared';

export type HistoryGranularity = 'month' | 'week';

const HistoryTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
      <p className="font-medium">{p.label}</p>
      <p className="text-muted-foreground">{fmtUSDCents(p.cost)}</p>
    </div>
  );
};

export function HistoryStrip({
  data,
  granularity,
  onGranularityChange,
  onLoadMore,
  canLoadMore,
  isLoading,
  emptyDescription,
}: {
  data?: FinopsHistory;
  granularity: HistoryGranularity;
  onGranularityChange: (g: HistoryGranularity) => void;
  onLoadMore: () => void;
  canLoadMore: boolean;
  isLoading?: boolean;
  emptyDescription?: string;
}) {
  const { t } = useTranslation();

  const series = granularity === 'month'
    ? (data?.monthly ?? []).map((p) => ({ key: p.month, label: fmtMonth(p.month), tick: fmtMonth(p.month), cost: p.cost }))
    : (data?.weekly ?? []).map((p) => ({ key: p.weekStart, label: fmtWeekRange(p.weekStart), tick: fmtDay(p.weekStart), cost: p.cost }));

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold">{t('finops.cloud.history_title')}</h3>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5">
            {(['month', 'week'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => onGranularityChange(g)}
                className={`px-2.5 py-1 text-xs rounded ${
                  granularity === g ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(g === 'month' ? 'finops.cloud.history_by_month' : 'finops.cloud.history_by_week')}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={!canLoadMore || isLoading}>
            {t('finops.cloud.history_load_more')}
          </Button>
        </div>
      </div>

      {series.length === 0 ? (
        <EmptyState
          title={t('finops.cloud.history_empty_title')}
          description={emptyDescription ?? t('finops.cloud.history_empty_description')}
        />
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="tick" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => fmtUSD(v)} tick={{ fontSize: 11 }} width={72} />
            <Tooltip content={<HistoryTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }} />
            <Bar dataKey="cost" fill="#6366f1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
